// Add Photo onboarding step — Step 1 of 3 in TRIMIE flow.
// Per docs/design-spec.md §4.2.
// Skippable. Sizing follows (§4.3).

import { useState, useRef } from 'react';
import api from './api';
import CameraCapture from './CameraCapture';
import Header from './components/Header';
import { compressImage } from './utils/compressImage';

export default function OnboardingPhotoPage({ onDone, onSkip, onBuildAvatar }) {
  const [showCamera, setShowCamera] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  // Onboarding asks for a full-body photo to use as the try-on model input.
  // Send kind=body so it stores into bodyPhotoUrl, not the avatar.
  // Compress + JPEG-encode client-side first to dodge the 2MB / HEIC trap.
  const uploadPhoto = async (file) => {
    setUploading(true);
    setError(null);
    setPreview(URL.createObjectURL(file));
    let prepared;
    try { prepared = await compressImage(file); }
    catch (err) {
      setError(err.message || 'Could not read that image.');
      setPreview(null);
      setUploading(false);
      return;
    }
    const form = new FormData();
    form.append('profile_photo', prepared);
    form.append('kind', 'body');
    try {
      await api.post('/auth/profile/upload-profile/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. You can add a photo later from your profile.');
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="auth-shell">
      {showCamera && (
        <CameraCapture
          label="Take a Selfie"
          onCapture={(f) => { setShowCamera(false); uploadPhoto(f); }}
          onClose={() => setShowCamera(false)}
        />
      )}

      <Header onBack={null} action={{ icon: <span>Skip</span>, onClick: onSkip, ariaLabel: 'Skip' }} />

      <div className="onb-body">
        <span className="mono-eyebrow">Step 1 of 3</span>
        <h1>Add a full-body photo.</h1>
        <p className="lead">Daylight, arms relaxed. We use this to fit garments to your body.</p>

        <div className={`onb-photo-well${preview ? ' filled' : ''}`}>
          {preview ? (
            <img src={preview} alt="" />
          ) : (
            <svg viewBox="0 0 100 200" width="50%" height="80%" preserveAspectRatio="xMidYMax meet" aria-hidden="true">
              <circle cx="50" cy="22" r="11" fill="var(--t-purple-soft)"/>
              <rect x="46" y="32" width="8" height="6" fill="var(--t-purple-soft)"/>
              <path d="M30 40 L70 40 L74 60 L72 100 L28 100 L26 60 Z" fill="var(--t-purple-soft)"/>
              <path d="M30 42 L18 80 L22 100 L26 98 L30 60Z" fill="var(--t-purple-soft)"/>
              <path d="M70 42 L82 80 L78 100 L74 98 L70 60Z" fill="var(--t-purple-soft)"/>
              <path d="M32 100 L36 175 L46 175 L48 100 Z" fill="var(--t-purple-soft)"/>
              <path d="M52 100 L54 175 L64 175 L68 100 Z" fill="var(--t-purple-soft)"/>
            </svg>
          )}
          <button
            className="onb-photo-fab"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            type="button"
            aria-label="Upload photo"
          >
            {uploading
              ? <span className="spinner" style={{ color: '#fff' }} />
              : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 15v3a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-3" />
                  <path d="M7 10l5-5 5 5" />
                  <path d="M12 5v12" />
                </svg>
              )
            }
          </button>
        </div>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => {
          const f = e.target.files[0];
          if (f) uploadPhoto(f);
        }} />

        {error && <div className="alert error">{error}</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-outline" style={{ flex: 1 }} onClick={() => setShowCamera(true)} disabled={uploading}>
            Camera
          </button>
          <button className="btn-outline" style={{ flex: 1 }} onClick={() => inputRef.current?.click()} disabled={uploading}>
            Library
          </button>
        </div>

        {onBuildAvatar && (
          <button
            type="button"
            className="link-inline"
            onClick={onBuildAvatar}
            disabled={uploading}
            style={{ marginTop: 4, alignSelf: 'flex-start' }}
          >
            Or build an avatar instead →
          </button>
        )}

        <div style={{ flex: 1 }} />

        <button className="btn-purple btn-lg" onClick={onSkip} disabled={uploading}>
          Continue
        </button>
      </div>
    </div>
  );
}
